'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, animate, motion, useMotionValue, useTransform } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Info,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import {
  HIDDEN_AMOUNT,
  setAmountsHidden,
  useAmountsHidden,
} from '@church/nextjs-ui/website/hide-amounts';
import { useWidgetConfig } from '@/lib/widget-config';
import {
  ApiError,
  type Congregation,
  type ExistingCommitment,
  type ProfileResponse,
  createCommitment,
  deleteCommitment,
  getContactImageUrl,
  getProfile,
  isSignedIn,
  replaceCommitment,
  subscribeToTokenChanges,
  triggerMPLogin,
  updateContact,
  updateStoredResources,
} from '@/lib/api-client';

type Step =
  | 'loading'
  | 'signed-out'
  | 'error'
  | 'intro'
  | 'details'
  | 'form'
  | 'calculating'
  | 'stored-resources';

interface CommittedResult {
  commitmentId: number;
  previous: number;
  expanded: number;
  projectedAnnual: number;
  projectedTwoYear: number;
  stored: number;
  total: number;
}

export function CommitmentForm() {
  const { campaignId } = useWidgetConfig();
  const [step, setStep] = useState<Step>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [previous, setPrevious] = useState('');
  const [expanded, setExpanded] = useState('');
  const [stored, setStored] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [committed, setCommitted] = useState<CommittedResult | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  // Selection state — who's on the next commitment. Stored as a single
  // object so the cross-state guard ("don't allow both off") runs inside a
  // functional setState and never reads stale closure values.
  const [scope, setScope] = useState<{ self: boolean; spouse: boolean }>({
    self: true,
    spouse: true,
  });
  const includeSelf = scope.self;
  const includeSpouse = scope.spouse;
  const setIncludeSelf = (v: boolean) => setScope((s) => ({ ...s, self: v }));
  const setIncludeSpouse = (v: boolean) => setScope((s) => ({ ...s, spouse: v }));
  // When commitments already exist, the intro shows the list. Clicking "Add
  // Another" flips this on, swapping the list view for the avatar / campus
  // picker so the user reaffirms scope before the form.
  const [pickingNew, setPickingNew] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [viewing, setViewing] = useState<ExistingCommitment | null>(null);

  // Loads the user's profile + commitments. Called on first mount and any
  // time the MP auth token changes (login in another tab, login via the
  // <mpp-user-login> modal, sign-out elsewhere).
  const loadProfile = useRef<((signal?: AbortSignal) => Promise<void>) | null>(null);
  loadProfile.current = async (signal?: AbortSignal) => {
    if (!isSignedIn()) {
      setProfile(null);
      setStep('signed-out');
      return;
    }
    setStep('loading');
    try {
      const p = await getProfile(campaignId, signal);
      setProfile(p);
      setStep('intro');
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      if (err instanceof ApiError && err.status === 401) {
        setProfile(null);
        setStep('signed-out');
      } else {
        setErrorMsg((err as Error).message || 'Failed to load');
        setStep('error');
      }
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    void loadProfile.current?.(ac.signal);
    // Re-fetch whenever the MP token changes — covers WP login via the
    // <mpp-user-login> modal, cross-tab login, and session expiry.
    const unsubscribe = subscribeToTokenChanges(() => {
      void loadProfile.current?.();
    });
    return () => {
      ac.abort();
      unsubscribe();
    };
  }, [campaignId]);

  // Centralized 401 handler — call this from any mutation's catch block. The
  // api-client already clears the token; we just need to flip UI state.
  function handleApiError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      setProfile(null);
      setSubmitting(false);
      setDeletingId(null);
      setStep('signed-out');
      return true;
    }
    return false;
  }

  const previousNum = useMemo(() => parseMoney(previous), [previous]);
  const expandedNum = useMemo(() => parseMoney(expanded), [expanded]);
  const projectedAnnual = previousNum + expandedNum;
  const projectedTwoYear = projectedAnnual * 2;
  const canSubmit = projectedAnnual > 0 && !submitting;

  function beginNew() {
    setEditingId(null);
    setPrevious('');
    setExpanded('');
    setStored('');
    setErrorMsg(null);
    setScope({ self: true, spouse: true });
    setPickingNew(true);
  }

  function continueToForm() {
    // beginEdit takes its own path straight to the form, so any time we end
    // up here we're starting a NEW commitment — make sure stale inputs from
    // a previous edit/create don't leak through.
    setEditingId(null);
    setPrevious('');
    setExpanded('');
    setStored('');
    setErrorMsg(null);
    setPickingNew(false);
    setStep('form');
  }

  function cancelPicking() {
    setPickingNew(false);
  }

  function beginEdit(c: ExistingCommitment) {
    setEditingId(c.commitmentId);
    setPrevious(String(c.previousAnnualGiving || ''));
    setExpanded(String(c.expandedAnnualGiving || ''));
    setStored(String(c.storedResourcesGiving || ''));
    // Reflect this commitment's scope so the headline / row preview is correct.
    setScope({
      self: profile?.contact.contactId === c.contactId,
      spouse: c.includeSpouseGiving || profile?.spouse?.contactId === c.contactId,
    });
    setPickingNew(false);
    setErrorMsg(null);
    setStep('form');
  }

  function onSubmit() {
    if (!canSubmit || !profile) return;
    setErrorMsg(null);
    setStep('calculating');
    // Fire the commit immediately — runs in parallel with the reveal animation.
    void runCommit();
  }

  // Returns a Promise that resolves to the next step. CalculatingStep waits on
  // BOTH this promise and its animation timer (Promise.all) before advancing,
  // so the user always sees the full reveal even on a fast network, and never
  // gets a flash of empty UI on a slow one.
  const commitPromiseRef = useRef<Promise<'stored-resources' | 'intro' | 'form'> | null>(null);
  function runCommit(): Promise<'stored-resources' | 'intro' | 'form'> {
    if (!profile) return Promise.resolve('form');
    setSubmitting(true);
    const p = (async () => {
      try {
        if (editingId) {
          const res = await replaceCommitment({
            commitmentId: editingId,
            previousAnnualGiving: previousNum,
            expandedAnnualGiving: expandedNum,
            storedResourcesGiving: parseMoney(stored),
          });
          setCommitted({
            commitmentId: editingId,
            previous: previousNum,
            expanded: expandedNum,
            projectedAnnual: res.projectedAnnualGiving,
            projectedTwoYear: res.projectedTwoYearGiving,
            stored: parseMoney(stored),
            total: res.totalTwoYearCommitment,
          });
          await refetch();
          return 'intro' as const;
        } else {
          // Snapshot the latest selection so we don't read stale closure
          // values mid-promise.
          const sel = scope;
          const includesBoth = sel.self && sel.spouse && !!profile.spouse;
          const onlySpouse = !sel.self && sel.spouse && !!profile.spouse;
          const forContactId = onlySpouse ? profile.spouse!.contactId : profile.contact.contactId;
          const res = await createCommitment({
            campaignId,
            forContactId,
            previousAnnualGiving: previousNum,
            expandedAnnualGiving: expandedNum,
            storedResourcesGiving: 0,
            includeSpouse: includesBoth,
            spouse:
              includesBoth && profile.spouse
                ? {
                    firstName: profile.spouse.firstName,
                    lastName: profile.spouse.lastName,
                    email: profile.spouse.email,
                    phone: profile.spouse.mobilePhone,
                  }
                : null,
          });
          setCommitted({
            commitmentId: res.commitmentId,
            previous: previousNum,
            expanded: expandedNum,
            projectedAnnual: res.projectedAnnualGiving,
            projectedTwoYear: res.projectedTwoYearGiving,
            stored: 0,
            total: res.totalTwoYearCommitment,
          });
          return 'stored-resources' as const;
        }
      } catch (err) {
        if (handleApiError(err)) return 'form' as const;
        setErrorMsg((err as Error).message);
        return 'form' as const;
      } finally {
        setSubmitting(false);
      }
    })();
    commitPromiseRef.current = p;
    return p;
  }

  async function onCalculationDone() {
    const next = (await commitPromiseRef.current) ?? 'form';
    setStep(next);
  }

  async function onAddStoredResources() {
    if (!committed) return;
    const amt = parseMoney(stored);
    if (amt <= 0) {
      await refetch();
      setCommitted(null);
      setStep('intro');
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await updateStoredResources(committed.commitmentId, amt);
      setCommitted({
        ...committed,
        stored: res.storedResourcesGiving,
        total: res.totalTwoYearCommitment,
      });
      await refetch();
      setCommitted(null);
      setStep('intro');
    } catch (err) {
      if (handleApiError(err)) return;
      setErrorMsg((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function refetch() {
    try {
      const p = await getProfile(campaignId);
      setProfile(p);
    } catch (err) {
      // A 401 during a background refresh still needs to bounce the user
      // out — otherwise they'd keep clicking "edit" against a dead session.
      handleApiError(err);
    }
  }

  async function onDelete(commitmentId: number) {
    setDeletingId(commitmentId);
    try {
      await deleteCommitment(commitmentId);
      await refetch();
    } catch (err) {
      if (handleApiError(err)) return;
      setErrorMsg((err as Error).message);
    } finally {
      setDeletingId(null);
    }
  }

  function viewDetails(c: ExistingCommitment) {
    setViewing(c);
    setStep('details');
  }

  // Called from DetailsStep: jump back to intro, then fire delete in
  // the background so the user sees the row's processing/fade state.
  function deleteFromDetails(c: ExistingCommitment) {
    setViewing(null);
    setStep('intro');
    void onDelete(c.commitmentId);
  }

  return (
    <Frame>
      <AnimatePresence mode="wait">
        {step === 'loading' && (
          <Slide key="loading">
            <Loading />
          </Slide>
        )}
        {step === 'signed-out' && (
          <Slide key="signed-out">
            <SignedOut />
          </Slide>
        )}
        {step === 'error' && (
          <Slide key="error">
            <ErrorView message={errorMsg} />
          </Slide>
        )}
        {step === 'intro' && profile && (
          <Slide key={pickingNew ? 'intro-pick' : 'intro'}>
            <IntroStep
              profile={profile}
              deletingId={deletingId}
              pickingNew={pickingNew}
              includeSelf={includeSelf}
              includeSpouse={includeSpouse}
              onToggleSelf={() => setScope((s) => (s.spouse ? { ...s, self: !s.self } : s))}
              onToggleSpouse={() => setScope((s) => (s.self ? { ...s, spouse: !s.spouse } : s))}
              onBegin={beginNew}
              onContinueToForm={continueToForm}
              onCancelPicking={cancelPicking}
              onView={viewDetails}
              onCampusSave={async (id) => {
                try {
                  await updateContact({ congregationId: id });
                  setProfile((p) =>
                    p ? { ...p, contact: { ...p.contact, congregationId: id } } : p
                  );
                } catch (err) {
                  if (handleApiError(err)) return;
                  throw err;
                }
              }}
            />
          </Slide>
        )}
        {step === 'details' && profile && viewing && (
          <Slide key="details">
            <DetailsStep
              profile={profile}
              commitment={viewing}
              onBack={() => {
                setViewing(null);
                setStep('intro');
              }}
              onDelete={() => deleteFromDetails(viewing)}
              onSave={async (vals) => {
                const res = await replaceCommitment({
                  commitmentId: viewing.commitmentId,
                  previousAnnualGiving: vals.previous,
                  expandedAnnualGiving: vals.expanded,
                  storedResourcesGiving: vals.stored,
                });
                // Update the viewing snapshot so the details page reflects
                // the new values without bouncing back to the list.
                setViewing({
                  ...viewing,
                  previousAnnualGiving: vals.previous,
                  expandedAnnualGiving: vals.expanded,
                  storedResourcesGiving: vals.stored,
                  projectedAnnualGiving: res.projectedAnnualGiving,
                  projectedTwoYearGiving: res.projectedTwoYearGiving,
                  totalTwoYearCommitment: res.totalTwoYearCommitment,
                });
                await refetch();
              }}
            />
          </Slide>
        )}
        {step === 'form' && profile && (
          <Slide key="form">
            <FormStep
              profile={profile}
              previous={previous}
              expanded={expanded}
              onPreviousChange={setPrevious}
              onExpandedChange={setExpanded}
              includeSelf={includeSelf}
              includeSpouse={includeSpouse}
              canSubmit={canSubmit}
              errorMsg={errorMsg}
              editing={editingId !== null}
              onBack={() => setStep('intro')}
              onSubmit={onSubmit}
            />
          </Slide>
        )}
        {step === 'calculating' && profile && (
          <Slide key="calculating">
            <CalculatingStep
              profile={profile}
              previous={previousNum}
              expanded={expandedNum}
              showSelf={includeSelf}
              showSpouse={includeSpouse}
              onReady={onCalculationDone}
              submitting={submitting}
            />
          </Slide>
        )}
        {step === 'stored-resources' && committed && (
          <Slide key="stored">
            <StoredResourcesStep
              committed={committed}
              stored={stored}
              onStoredChange={setStored}
              onSkip={async () => {
                await refetch();
                setCommitted(null);
                setStep('intro');
              }}
              onAdd={onAddStoredResources}
              submitting={submitting}
              errorMsg={errorMsg}
            />
          </Slide>
        )}
      </AnimatePresence>
    </Frame>
  );
}

// ───────────────────────── Frame + transitions ─────────────────────────

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="commitment-frame mx-auto w-full">
      <span className="commitment-frame-stripe t" />
      <span className="commitment-frame-stripe r" />
      <span className="commitment-frame-stripe b" />
      <span className="commitment-frame-stripe l" />
      <span className="commitment-frame-corner tl" />
      <span className="commitment-frame-corner tr" />
      <span className="commitment-frame-corner bl" />
      <span className="commitment-frame-corner br" />
      <div className="commitment-frame-inner">{children}</div>
    </div>
  );
}

function Slide({ children }: { children: React.ReactNode; key?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ───────────────────────── Steps ─────────────────────────

function IntroStep({
  profile,
  deletingId,
  pickingNew,
  includeSelf,
  includeSpouse,
  onToggleSelf,
  onToggleSpouse,
  onBegin,
  onContinueToForm,
  onCancelPicking,
  onView,
  onCampusSave,
}: {
  profile: ProfileResponse;
  deletingId: number | null;
  pickingNew: boolean;
  includeSelf: boolean;
  includeSpouse: boolean;
  onToggleSelf: () => void;
  onToggleSpouse: () => void;
  onBegin: () => void;
  onContinueToForm: () => void;
  onCancelPicking: () => void;
  onView: (c: ExistingCommitment) => void;
  onCampusSave: (id: number) => Promise<void>;
}) {
  const hasCommitments = profile.existingCommitments.length > 0;
  // Show the picker view when there are no commitments yet, OR when the user
  // has explicitly opted in via "Add Another Commitment".
  const showPicker = !hasCommitments || pickingNew;

  return (
    <div className="relative space-y-5">
      <LogoMark />
      <SectionTitle>See Jesus</SectionTitle>

      {showPicker && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="flex flex-wrap items-start justify-center gap-5"
        >
          <PersonAvatar
            firstName={profile.contact.firstName}
            lastName={profile.contact.lastName}
            imageGuid={profile.contact.imageGuid}
            selected={includeSelf}
            selectable={!!profile.spouse}
            onToggle={onToggleSelf}
          />
          {profile.spouse && (
            <PersonAvatar
              firstName={profile.spouse.firstName}
              lastName={profile.spouse.lastName}
              imageGuid={profile.spouse.imageGuid}
              selected={includeSpouse}
              selectable
              onToggle={onToggleSpouse}
            />
          )}
          <CampusCircle
            currentId={profile.contact.congregationId}
            congregations={profile.congregations}
            onSelect={onCampusSave}
          />
        </motion.div>
      )}

      {hasCommitments && !pickingNew && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="space-y-2"
        >
          <div className="text-center">
            <p className="font-display text-foreground text-2xl">Thank you.</p>
            <p className="text-secondary mt-1 text-[11px] tracking-widest uppercase">
              {commitmentsHeading(profile)}
            </p>
            <div className="mt-2 flex justify-center">
              <HideAmountsToggle />
            </div>
          </div>
          <ul className="space-y-2">
            <AnimatePresence initial={false}>
              {profile.existingCommitments.map((c) => (
                <motion.div
                  key={c.commitmentId}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
                  transition={{ duration: 0.25 }}
                  style={{ overflow: 'hidden' }}
                >
                  <CommitmentRow
                    commitment={c}
                    profile={profile}
                    deleting={deletingId === c.commitmentId}
                    onClick={() => onView(c)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </ul>
        </motion.div>
      )}

      {showPicker && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: hasCommitments ? 0.28 : 0.2, duration: 0.4 }}
          className="space-y-2"
        >
          <button onClick={onContinueToForm} className="btn-double-outline">
            <span>Start Commitment</span>
            <ArrowRight className="arrow h-5 w-5" strokeWidth={1.5} />
          </button>
          {pickingNew && (
            <button
              onClick={onCancelPicking}
              className="text-secondary hover:text-foreground w-full py-2 text-center text-sm transition-colors"
            >
              Cancel
            </button>
          )}
        </motion.div>
      )}
    </div>
  );
}

function FormStep({
  profile,
  previous,
  expanded,
  onPreviousChange,
  onExpandedChange,
  includeSelf,
  includeSpouse,
  canSubmit,
  errorMsg,
  editing,
  onBack,
  onSubmit,
}: {
  profile: ProfileResponse;
  previous: string;
  expanded: string;
  onPreviousChange: (v: string) => void;
  onExpandedChange: (v: string) => void;
  includeSelf: boolean;
  includeSpouse: boolean;
  canSubmit: boolean;
  errorMsg: string | null;
  editing: boolean;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const [chartOpen, setChartOpen] = useState(false);

  // The NavRow on the form step echoes who's on this commitment (the user
  // already chose on the intro), so we show only the selected mini-avatars.
  // The chart icon sits in the right slot — top-right of the form.
  return (
    <div className="space-y-5">
      <NavRow
        profile={profile}
        onBack={onBack}
        showSelf={includeSelf}
        showSpouse={includeSpouse}
        rightSlot={
          <button
            type="button"
            onClick={() => setChartOpen(true)}
            className="btn-foil-circle"
            aria-label="See giving chart"
            title="See giving chart"
          >
            <BarChart3 className="h-4 w-4" strokeWidth={2.25} />
          </button>
        }
      />
      <SectionTitle>Your Commitment</SectionTitle>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.35 }}
      >
        <DarkMoneyInput
          label="About how much do you give per year?"
          tooltip="Combine your regular giving with any above-and-beyond gifts from the past year."
          value={previous}
          onChange={onPreviousChange}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.35 }}
      >
        <DarkMoneyInput
          label="Your annual See Jesus gift"
          tooltip="The extra you'll give each year for See Jesus, above your normal giving. We'll multiply this by 2 years for your total commitment."
          value={expanded}
          onChange={onExpandedChange}
        />
      </motion.div>

      {errorMsg && <p className="text-destructive text-center text-sm">{errorMsg}</p>}

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.22, duration: 0.35 }}
      >
        <button onClick={onSubmit} disabled={!canSubmit} className="btn-double-outline">
          <span>Submit</span>
          <ArrowRight className="arrow h-5 w-5" strokeWidth={1.5} />
        </button>
      </motion.div>

      <GivingChartSheet open={chartOpen} onClose={() => setChartOpen(false)} />
    </div>
  );
}

function CalculatingStep({
  profile,
  previous,
  expanded,
  showSelf,
  showSpouse,
  onReady,
  submitting,
}: {
  profile: ProfileResponse;
  previous: number;
  expanded: number;
  showSelf: boolean;
  showSpouse: boolean;
  onReady: () => void;
  submitting: boolean;
}) {
  const projectedAnnual = previous + expanded;
  const total = projectedAnnual * 2;

  // Kick off the commit POST as soon as the user lands on this step. The
  // reveal animation runs in parallel; we advance once the animation has
  // ended AND the final total has had time to breathe on screen.
  useEffect(() => {
    const t = setTimeout(() => onReady(), 4500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-5 py-2 text-center">
      <NavRow
        profile={profile}
        onBack={() => {}}
        hideBack
        showSelf={showSelf}
        showSpouse={showSpouse}
      />
      <SectionTitle>Your Two-Year Commitment</SectionTitle>

      <div className="mt-2 space-y-3">
        <RevealLine delay={0.0}>
          <span className="text-secondary text-xs tracking-widest uppercase">Your annual gift</span>
          <CountUp
            target={projectedAnnual}
            duration={0.7}
            className="font-display text-foreground mt-1 block text-4xl"
          />
        </RevealLine>

        <RevealLine delay={0.9}>
          <span className="font-display text-secondary text-xl">× 2 years</span>
        </RevealLine>

        <RevealLine delay={1.6}>
          <CountUp
            target={total}
            duration={1.0}
            className="font-display text-foreground mt-1 block text-6xl"
          />
        </RevealLine>
      </div>
    </div>
  );
}

function RevealLine({ delay, children }: { delay: number; children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function CountUp({
  target,
  duration = 1,
  className,
}: {
  target: number;
  duration?: number;
  className?: string;
}) {
  const mv = useMotionValue(0);
  const display = useTransform(mv, (n) =>
    Math.round(n).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  );

  useEffect(() => {
    const controls = animate(mv, target, { duration, ease: [0.22, 1, 0.36, 1] });
    return () => controls.stop();
  }, [target, duration, mv]);

  return <motion.span className={className}>{display}</motion.span>;
}

function StoredResourcesStep({
  committed,
  stored,
  onStoredChange,
  onSkip,
  onAdd,
  submitting,
  errorMsg,
}: {
  committed: CommittedResult;
  stored: string;
  onStoredChange: (v: string) => void;
  onSkip: () => void;
  onAdd: () => Promise<void>;
  submitting: boolean;
  errorMsg: string | null;
}) {
  const [showTip, setShowTip] = useState(false);
  const parsed = parseMoney(stored);

  return (
    <div className="space-y-5">
      <SectionTitle>Stored Resources</SectionTitle>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="text-center"
      >
        <p className="text-secondary text-[11px] tracking-widest uppercase">Two-Year Commitment</p>
        <p className="font-display text-foreground mt-0.5 text-4xl">
          {formatMoney(committed.total)}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.35 }}
      >
        <DarkMoneyInput
          label="Add a gift from stored resources?"
          tooltip="Stored resources are gifts from assets you already own — stocks, real estate, retirement accounts, business interests — given outside your regular income."
          value={stored}
          onChange={onStoredChange}
        />
      </motion.div>

      {errorMsg && <p className="text-destructive text-center text-sm">{errorMsg}</p>}

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.35 }}
        className="space-y-2"
      >
        <button onClick={onAdd} disabled={submitting || parsed <= 0} className="btn-double-outline">
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Adding…</span>
            </>
          ) : (
            <>
              <span>Add to commitment</span>
              <ArrowRight className="arrow h-5 w-5" strokeWidth={1.5} />
            </>
          )}
        </button>
        <button
          onClick={onSkip}
          disabled={submitting}
          className="text-secondary hover:text-foreground w-full py-2 text-center text-sm transition-colors disabled:opacity-50"
        >
          Skip — I'm done
        </button>
      </motion.div>
    </div>
  );
}

// ───────────────────────── Pieces ─────────────────────────

// Vite bakes this in at build time; empty in next dev so /assets/... resolves
// against the dev server. Same value as in widget/index.tsx.
const ASSET_BASE_URL = (process.env.CHURCH_HUB_WIDGET_ASSET_BASE_URL || '').replace(/\/$/, '');

function LogoMark() {
  // Negative offsets pull the logo out of the step container's normal flow
  // and tuck it into the top-left corner of the inner pane (whose padding
  // is 24px top / 22px left — match those values to kiss the gold border).
  return (
    <img
      src={`${ASSET_BASE_URL}/assets/Logo.png`}
      alt="See Jesus"
      className="pointer-events-none absolute -top-[24px] -left-[22px] h-auto w-28 select-none"
    />
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <motion.h1
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="see-jesus-hero-title text-center"
    >
      {children}
    </motion.h1>
  );
}

function NavRow({
  profile,
  onBack,
  hideBack = false,
  showSelf = true,
  showSpouse = true,
  rightSlot,
}: {
  profile: ProfileResponse;
  onBack: () => void;
  hideBack?: boolean;
  showSelf?: boolean;
  showSpouse?: boolean;
  /** Optional right-side slot. Defaults to a spacer that balances the
   *  back button so the avatars stay visually centered. */
  rightSlot?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      {hideBack ? (
        <div className="w-12" />
      ) : (
        <button
          onClick={onBack}
          className="text-secondary hover:text-foreground flex items-center gap-1 text-sm transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
      )}
      <div className="flex items-center gap-2">
        {showSelf && (
          <MiniAvatar firstName={profile.contact.firstName} imageGuid={profile.contact.imageGuid} />
        )}
        {showSpouse && profile.spouse && (
          <MiniAvatar firstName={profile.spouse.firstName} imageGuid={profile.spouse.imageGuid} />
        )}
      </div>
      {rightSlot ?? <div className="w-12" />}
    </div>
  );
}

function DarkMoneyInput({
  label,
  tooltip,
  value,
  onChange,
}: {
  label: string;
  tooltip?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [showTip, setShowTip] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape so the popover stays out of the way.
  useEffect(() => {
    if (!showTip) return;
    function onClick(e: MouseEvent) {
      const t = e.composedPath()[0] as Node;
      if (wrapRef.current && !wrapRef.current.contains(t)) setShowTip(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowTip(false);
    }
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [showTip]);

  return (
    <div>
      <div ref={wrapRef} className="relative mb-2 flex items-center gap-2">
        <span className="text-foreground text-xs font-semibold tracking-widest uppercase">
          {label}
        </span>
        {tooltip && (
          <button
            type="button"
            onClick={() => setShowTip((s) => !s)}
            aria-label="Help"
            aria-expanded={showTip}
            className="text-secondary hover:text-foreground transition-colors"
          >
            <Info className="h-4 w-4" />
          </button>
        )}
        <AnimatePresence>
          {showTip && tooltip && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              role="tooltip"
              className="bg-card text-card-foreground border-secondary/40 absolute top-full right-0 left-0 z-30 mt-2 max-w-sm rounded-md border px-3 py-2 text-xs shadow-2xl"
            >
              {tooltip}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="relative">
        <span
          className="text-secondary font-display absolute top-1/2 left-3 -translate-y-1/2 text-xl"
          aria-hidden
        >
          $
        </span>
        <input
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(sanitizeMoneyInput(e.target.value))}
          placeholder="0"
          className="border-secondary/50 font-display text-foreground placeholder:text-secondary/50 focus:border-secondary focus:ring-secondary w-full rounded-md border bg-transparent py-2.5 pr-3 pl-9 text-xl transition-colors focus:ring-1 focus:outline-none"
        />
      </div>
    </div>
  );
}

function SpouseToggle({
  spouseFirstName,
  spouseImageGuid,
  userFirstName,
  userImageGuid,
  includeSpouse,
  onChange,
}: {
  spouseFirstName: string;
  spouseImageGuid: string | null;
  userFirstName: string;
  userImageGuid: string | null;
  includeSpouse: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="border-secondary/40 inline-flex items-center gap-1 rounded-full border bg-transparent p-1">
      <ToggleOption
        firstName={userFirstName}
        imageGuid={userImageGuid}
        label="Just me"
        selected={!includeSpouse}
        onClick={() => onChange(false)}
        secondImageGuid={null}
        secondFirstName={null}
      />
      <ToggleOption
        firstName={userFirstName}
        imageGuid={userImageGuid}
        secondFirstName={spouseFirstName}
        secondImageGuid={spouseImageGuid}
        label="Together"
        selected={includeSpouse}
        onClick={() => onChange(true)}
      />
    </div>
  );
}

function ToggleOption({
  firstName,
  imageGuid,
  secondFirstName,
  secondImageGuid,
  label,
  selected,
  onClick,
}: {
  firstName: string;
  imageGuid: string | null;
  secondFirstName: string | null;
  secondImageGuid: string | null;
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
        selected
          ? 'bg-secondary text-secondary-foreground'
          : 'text-foreground/70 hover:text-foreground'
      }`}
    >
      <span className="flex -space-x-1.5">
        <RowAvatarMini firstName={firstName} imageGuid={imageGuid} />
        {secondFirstName && (
          <RowAvatarMini firstName={secondFirstName} imageGuid={secondImageGuid} />
        )}
      </span>
      <span>{label}</span>
    </button>
  );
}

function RowAvatarMini({ firstName, imageGuid }: { firstName: string; imageGuid: string | null }) {
  const url = getContactImageUrl(imageGuid);
  const [err, setErr] = useState(false);
  if (url && !err) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setErr(true)}
        className="h-5 w-5 rounded-full border border-current object-cover"
      />
    );
  }
  return (
    <div className="flex h-5 w-5 items-center justify-center rounded-full border border-current bg-transparent text-[9px] font-semibold">
      {firstName?.[0]?.toUpperCase() ?? '—'}
    </div>
  );
}

function PersonAvatar({
  firstName,
  lastName,
  imageGuid,
  selected = true,
  selectable = false,
  onToggle,
}: {
  firstName: string;
  lastName: string;
  imageGuid: string | null;
  selected?: boolean;
  selectable?: boolean;
  onToggle?: () => void;
}) {
  const url = getContactImageUrl(imageGuid);
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  const [err, setErr] = useState(false);

  // Empty "add me" state — replaces the photo AND name entirely so the row
  // reads as a clear empty placeholder.
  if (selectable && !selected) {
    return (
      <button
        type="button"
        onClick={onToggle}
        title={`Add ${firstName}`}
        className="group flex flex-col items-center focus:outline-none"
        aria-pressed={false}
        aria-label={`Add ${firstName} to the commitment`}
      >
        <div className="border-secondary/50 text-secondary/70 hover:border-secondary hover:text-secondary hover:bg-secondary/5 flex h-20 w-20 items-center justify-center rounded-full border-2 border-dashed transition-colors">
          <Plus className="h-7 w-7" strokeWidth={1.5} />
        </div>
        {/* Spacer keeps the row vertically aligned with the other (named) circles */}
        <span className="invisible mt-2 text-sm font-semibold select-none" aria-hidden>
          {firstName}
        </span>
      </button>
    );
  }

  const photo =
    url && !err ? (
      <img
        src={url}
        alt=""
        onError={() => setErr(true)}
        className="border-secondary group-hover:border-secondary h-20 w-20 rounded-full border-2 object-cover transition-all group-hover:brightness-50"
      />
    ) : (
      <div className="border-secondary bg-secondary/10 font-display text-secondary flex h-20 w-20 items-center justify-center rounded-full border-2 text-2xl transition-all group-hover:brightness-75">
        {initials || '—'}
      </div>
    );

  if (selectable && onToggle) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="group flex flex-col items-center focus:outline-none"
        aria-pressed={selected}
        aria-label={`Remove ${firstName} from the commitment`}
      >
        <div className="relative">
          {photo}
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            <span className="border-secondary flex h-8 w-8 items-center justify-center rounded-full border bg-black/80">
              <X className="text-secondary h-4 w-4" strokeWidth={2} />
            </span>
          </span>
        </div>
        <p className="text-foreground group-hover:text-secondary mt-2 text-sm font-semibold transition-colors">
          {firstName}
        </p>
      </button>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {photo}
      <p className="text-foreground mt-2 text-sm font-semibold">{firstName}</p>
    </div>
  );
}

function MiniAvatar({ firstName, imageGuid }: { firstName: string; imageGuid: string | null }) {
  const url = getContactImageUrl(imageGuid);
  const [err, setErr] = useState(false);
  if (url && !err) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setErr(true)}
        className="border-secondary h-7 w-7 rounded-full border object-cover"
      />
    );
  }
  return (
    <div className="border-secondary bg-secondary/10 text-secondary flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-semibold">
      {firstName?.[0]?.toUpperCase() ?? '—'}
    </div>
  );
}

function CampusCircle({
  currentId,
  congregations,
  onSelect,
}: {
  currentId: number | null;
  congregations: Congregation[];
  onSelect: (id: number) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const current = congregations.find((c) => c.Congregation_ID === currentId) ?? null;

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const target = e.composedPath()[0] as Node;
      if (containerRef.current && !containerRef.current.contains(target)) setOpen(false);
    }
    document.addEventListener('click', handle, true);
    return () => document.removeEventListener('click', handle, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handle(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);

  const handlePick = async (id: number) => {
    setOpen(false);
    await onSelect(id);
  };

  return (
    <div ref={containerRef} className="relative flex flex-col items-center">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="border-secondary bg-secondary/5 hover:bg-secondary/10 flex h-20 w-20 items-center justify-center rounded-full border-2 transition-colors"
      >
        {current?.Campus_SVG_URL ? (
          <img src={current.Campus_SVG_URL} alt="" className="h-12 w-12 object-contain" />
        ) : (
          <MapPin className="text-secondary h-8 w-8" />
        )}
      </button>
      <button
        onClick={() => setOpen((o) => !o)}
        className="text-foreground hover:text-secondary mt-2 flex items-center gap-1 text-sm font-semibold transition-colors"
      >
        <span>{current?.Congregation_Name ?? 'Select campus'}</span>
        <ChevronDown
          className={`text-secondary h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={1.5}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            role="listbox"
            className="bg-card text-card-foreground border-secondary/40 absolute top-full left-1/2 z-50 mt-2 max-h-[360px] w-64 -translate-x-1/2 overflow-y-auto rounded border shadow-2xl"
          >
            <div className="space-y-0.5 p-2">
              {congregations.map((c) => {
                const selected = c.Congregation_ID === currentId;
                return (
                  <button
                    key={c.Congregation_ID}
                    onClick={() => handlePick(c.Congregation_ID)}
                    role="option"
                    aria-selected={selected}
                    className={`flex w-full items-center gap-3 rounded px-2 py-2 text-left transition-colors ${
                      selected ? 'bg-secondary/15' : 'hover:bg-secondary/10'
                    }`}
                  >
                    {c.Campus_SVG_URL ? (
                      <img
                        src={c.Campus_SVG_URL}
                        alt=""
                        className="h-7 w-7 shrink-0 object-contain"
                      />
                    ) : (
                      <MapPin className="text-muted-foreground h-5 w-5 shrink-0" />
                    )}
                    <span
                      className={`flex-1 text-sm font-medium ${
                        selected ? 'text-secondary' : 'text-card-foreground'
                      }`}
                    >
                      {c.Congregation_Name}
                    </span>
                    {selected && <Check className="text-secondary h-4 w-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Legacy bar-style picker — kept for any other consumer; intro now uses CampusCircle.
function CampusPicker({
  currentId,
  congregations,
  onSelect,
}: {
  currentId: number | null;
  congregations: Congregation[];
  onSelect: (id: number) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const current = congregations.find((c) => c.Congregation_ID === currentId) ?? null;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const target = e.composedPath()[0] as Node;
      if (containerRef.current && !containerRef.current.contains(target)) setOpen(false);
    }
    document.addEventListener('click', handle, true);
    return () => document.removeEventListener('click', handle, true);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handle(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);

  const handlePick = async (id: number) => {
    setOpen(false);
    await onSelect(id);
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        onClick={() => setOpen((o) => !o)}
        className="text-foreground hover:text-secondary flex items-center gap-2.5 rounded px-2 py-1.5 transition-colors"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {current?.Campus_SVG_URL ? (
          <img src={current.Campus_SVG_URL} alt="" className="h-7 w-7 object-contain" />
        ) : (
          <MapPin className="text-secondary h-6 w-6" />
        )}
        <span className="text-secondary text-xs tracking-widest uppercase">Campus</span>
        <span className="text-sm font-semibold">{current?.Congregation_Name ?? 'Select'}</span>
        <ChevronDown
          className={`text-secondary h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={1.5}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            role="listbox"
            className="bg-card text-card-foreground border-secondary/40 absolute left-1/2 z-50 mt-2 max-h-[360px] w-64 -translate-x-1/2 overflow-y-auto rounded border shadow-2xl"
          >
            <div className="space-y-0.5 p-2">
              {congregations.map((c) => {
                const selected = c.Congregation_ID === currentId;
                return (
                  <button
                    key={c.Congregation_ID}
                    onClick={() => handlePick(c.Congregation_ID)}
                    role="option"
                    aria-selected={selected}
                    className={`flex w-full items-center gap-3 rounded px-2 py-2 text-left transition-colors ${
                      selected ? 'bg-secondary/15' : 'hover:bg-secondary/10'
                    }`}
                  >
                    {c.Campus_SVG_URL ? (
                      <img
                        src={c.Campus_SVG_URL}
                        alt=""
                        className="h-7 w-7 shrink-0 object-contain"
                      />
                    ) : (
                      <MapPin className="text-muted-foreground h-5 w-5 shrink-0" />
                    )}
                    <span
                      className={`flex-1 text-sm font-medium ${
                        selected ? 'text-secondary' : 'text-card-foreground'
                      }`}
                    >
                      {c.Congregation_Name}
                    </span>
                    {selected && <Check className="text-secondary h-4 w-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CommitmentRow({
  commitment,
  profile,
  deleting,
  onClick,
}: {
  commitment: ExistingCommitment;
  profile: ProfileResponse;
  deleting: boolean;
  onClick: () => void;
}) {
  // Resolve which avatars to show based on whose commitment this is.
  const isMine = commitment.contactId === profile.contact.contactId;
  const isSpouses = !!profile.spouse && commitment.contactId === profile.spouse.contactId;
  const both = isMine && commitment.includeSpouseGiving && !!profile.spouse;
  const primary = isMine ? profile.contact : isSpouses ? profile.spouse! : profile.contact;
  const hidden = useAmountsHidden();
  return (
    <li>
      <button
        onClick={onClick}
        disabled={deleting}
        className={`group commitment-row-frame ${deleting ? 'pointer-events-none opacity-60' : ''}`}
        aria-label={
          hidden
            ? 'View commitment'
            : `View commitment of ${formatMoney(commitment.totalTwoYearCommitment)}`
        }
      >
        <span className="commitment-row-frame-inner">
          <span className="flex shrink-0 -space-x-2">
            <RowAvatar firstName={primary.firstName} imageGuid={primary.imageGuid} />
            {both && profile.spouse && (
              <RowAvatar
                firstName={profile.spouse.firstName}
                imageGuid={profile.spouse.imageGuid}
              />
            )}
          </span>
          <span className="font-display text-foreground min-w-0 flex-1 text-left text-3xl leading-tight">
            {hidden ? HIDDEN_AMOUNT : formatMoney(commitment.totalTwoYearCommitment)}
          </span>
          {deleting ? (
            <Loader2 className="text-secondary h-4 w-4 shrink-0 animate-spin" />
          ) : (
            <ChevronRight
              className="text-secondary h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5"
              strokeWidth={1.5}
            />
          )}
        </span>
      </button>
    </li>
  );
}

function RowAvatar({ firstName, imageGuid }: { firstName: string; imageGuid: string | null }) {
  const url = getContactImageUrl(imageGuid);
  const [err, setErr] = useState(false);
  if (url && !err) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setErr(true)}
        className="border-secondary h-10 w-10 rounded-full border-2 bg-black object-cover"
      />
    );
  }
  return (
    <div className="border-secondary bg-secondary/10 text-secondary flex h-10 w-10 items-center justify-center rounded-full border-2 text-xs font-semibold">
      {firstName?.[0]?.toUpperCase() ?? '—'}
    </div>
  );
}

function DetailsStep({
  profile,
  commitment,
  onBack,
  onDelete,
  onSave,
}: {
  profile: ProfileResponse;
  commitment: ExistingCommitment;
  onBack: () => void;
  onDelete: () => void;
  onSave: (vals: { previous: number; expanded: number; stored: number }) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [previous, setPrevious] = useState(String(commitment.previousAnnualGiving || ''));
  const [expanded, setExpanded] = useState(String(commitment.expandedAnnualGiving || ''));
  const [stored, setStored] = useState(String(commitment.storedResourcesGiving || ''));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The profile's "Hide amounts" switch, shared with this widget. Editing shows
  // the figures regardless: you can't change numbers you can't see.
  const masked = useAmountsHidden() && !editing;

  // Live values: while editing, derive from inputs; otherwise from the row.
  const previousNum = editing ? parseMoney(previous) : commitment.previousAnnualGiving;
  const expandedNum = editing ? parseMoney(expanded) : commitment.expandedAnnualGiving;
  const storedNum = editing ? parseMoney(stored) : commitment.storedResourcesGiving;
  const annual = previousNum + expandedNum;
  const twoYear = annual * 2;
  const total = twoYear + storedNum;

  const hasChanges =
    previousNum !== commitment.previousAnnualGiving ||
    expandedNum !== commitment.expandedAnnualGiving ||
    storedNum !== commitment.storedResourcesGiving;

  function enterEdit() {
    setPrevious(String(commitment.previousAnnualGiving || ''));
    setExpanded(String(commitment.expandedAnnualGiving || ''));
    setStored(String(commitment.storedResourcesGiving || ''));
    setErr(null);
    setEditing(true);
  }

  function exitEdit() {
    setEditing(false);
    setErr(null);
  }

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await onSave({ previous: previousNum, expanded: expandedNum, stored: storedNum });
      setEditing(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // Mirror who's actually on this commitment in the top nav.
  const isMine = commitment.contactId === profile.contact.contactId;
  const isSpouses = !!profile.spouse && commitment.contactId === profile.spouse.contactId;
  const showSelf = isMine || isSpouses;
  // Only render the spouse slot when it's MY commitment and the spouse was
  // explicitly included. For a spouse-only commitment, the self slot already
  // displays the spouse (via the navProfile swap below), so the spouse slot
  // would render the same person twice.
  const showSpouse = isMine && commitment.includeSpouseGiving;
  // When the commitment is the spouse's, the "self" slot displays the
  // spouse — NavRowWithAction renders `profile.contact` in that slot, so
  // swap in the spouse's data.
  const navProfile =
    isSpouses && profile.spouse
      ? { ...profile, contact: { ...profile.contact, ...profile.spouse } }
      : profile;

  return (
    <div className="space-y-5">
      <NavRowWithAction
        profile={navProfile}
        onBack={editing ? exitEdit : onBack}
        backMode={editing ? 'cancel' : 'back'}
        showSelf={showSelf}
        showSpouse={showSpouse}
        action={
          editing ? (
            <SmallIconButton
              onClick={onDelete}
              label="Delete commitment"
              variant="danger"
              disabled={saving}
            >
              <Trash2 className="h-4 w-4" />
            </SmallIconButton>
          ) : (
            <SmallIconButton onClick={enterEdit} label="Edit commitment">
              <Pencil className="h-4 w-4" />
            </SmallIconButton>
          )
        }
      />
      <SectionTitle>Commitment Details</SectionTitle>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.35 }}
        className="space-y-1 text-center"
      >
        <p className="text-secondary text-[11px] tracking-widest uppercase">Two-Year Commitment</p>
        <p className="font-display text-foreground text-6xl">
          {masked ? HIDDEN_AMOUNT : formatMoney(total)}
        </p>
        {!editing && (
          <div className="flex justify-center pt-1">
            <HideAmountsToggle />
          </div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.35 }}
        className="mx-auto max-w-sm space-y-3"
      >
        <EquationLine
          editing={editing}
          masked={masked}
          aLabel="Annual Giving"
          aValue={previousNum}
          aInput={previous}
          onAInput={setPrevious}
          operator="+"
          bLabel="See Jesus"
          bValue={expandedNum}
          bInput={expanded}
          onBInput={setExpanded}
          result={annual}
          resultLabel="Per Year"
        />
        <EquationLine
          editing={editing}
          masked={masked}
          aLabel="2-Year"
          aValue={twoYear}
          operator="+"
          bLabel="Stored Resources"
          bValue={storedNum}
          bInput={stored}
          onBInput={setStored}
          result={total}
          resultLabel="Grand Total"
        />
      </motion.div>

      {err && <p className="text-destructive text-center text-sm">{err}</p>}

      {editing && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <button onClick={save} disabled={saving || !hasChanges} className="btn-double-outline">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving…</span>
              </>
            ) : (
              <>
                <span>Save Changes</span>
                <ArrowRight className="arrow h-5 w-5" strokeWidth={1.5} />
              </>
            )}
          </button>
        </motion.div>
      )}
    </div>
  );
}

function NavRowWithAction({
  profile,
  onBack,
  action,
  backMode = 'back',
  showSelf = true,
  showSpouse = true,
}: {
  profile: ProfileResponse;
  onBack: () => void;
  action: React.ReactNode;
  backMode?: 'back' | 'cancel';
  showSelf?: boolean;
  showSpouse?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      {backMode === 'cancel' ? (
        <SmallIconButton onClick={onBack} label="Cancel edit">
          <X className="h-4 w-4" />
        </SmallIconButton>
      ) : (
        <button
          onClick={onBack}
          className="text-secondary hover:text-foreground flex items-center gap-1 text-sm transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
      )}
      <div className="flex items-center gap-2">
        {showSelf && (
          <MiniAvatar firstName={profile.contact.firstName} imageGuid={profile.contact.imageGuid} />
        )}
        {showSpouse && profile.spouse && (
          <MiniAvatar firstName={profile.spouse.firstName} imageGuid={profile.spouse.imageGuid} />
        )}
      </div>
      {action}
    </div>
  );
}

/**
 * One row of the visual equation. Renders:
 *   [a] [operator] [b] = [result] (resultLabel)
 *
 * When `editing` is true and an input handler is provided for the relevant
 * side, the value renders as an editable money input instead of static text.
 */
function EquationLine({
  editing = false,
  masked = false,
  aLabel,
  aValue,
  aInput,
  onAInput,
  operator,
  bLabel,
  bValue,
  bInput,
  bDisplay,
  onBInput,
  result,
  resultLabel,
}: {
  editing?: boolean;
  /** "Hide amounts" is on: dots instead of every figure. */
  masked?: boolean;
  aLabel?: string;
  aValue: number;
  aInput?: string;
  onAInput?: (v: string) => void;
  operator: '+' | '×';
  bLabel?: string;
  bValue: number;
  bInput?: string;
  bDisplay?: string;
  onBInput?: (v: string) => void;
  result: number;
  resultLabel?: string;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-start gap-2 text-base">
      <EqCell label={aLabel}>
        <EqValue
          editing={editing && !!onAInput}
          masked={masked}
          value={aValue}
          input={aInput}
          onInput={onAInput}
        />
      </EqCell>
      <span className="text-secondary font-display pt-1.5 text-lg">{operator}</span>
      <EqCell label={bLabel}>
        <EqValue
          editing={editing && !!onBInput}
          masked={masked}
          value={bValue}
          input={bInput}
          onInput={onBInput}
          display={bDisplay}
        />
      </EqCell>
      <span className="text-secondary pt-1.5">=</span>
      <EqCell label={resultLabel} align="right">
        <p className="font-display text-foreground text-right">
          {masked ? HIDDEN_AMOUNT : formatMoney(result)}
        </p>
      </EqCell>
    </div>
  );
}

function EqCell({
  label,
  children,
}: {
  label?: string;
  /** Retained for API stability — labels are always right-aligned to match the right-aligned values. */
  align?: 'left' | 'right';
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col">
      {children}
      {label && (
        <span className="text-secondary mt-1 text-right text-[10px] leading-tight tracking-widest uppercase">
          {label}
        </span>
      )}
    </div>
  );
}

function EqValue({
  editing = false,
  masked = false,
  value,
  input,
  onInput,
  display,
}: {
  editing?: boolean;
  masked?: boolean;
  value: number;
  input?: string;
  onInput?: (v: string) => void;
  display?: string;
}) {
  if (editing && onInput !== undefined) {
    return (
      <div className="relative">
        <span
          className="text-secondary font-display absolute top-1/2 left-2 -translate-y-1/2 text-sm"
          aria-hidden
        >
          $
        </span>
        <input
          inputMode="decimal"
          value={input ?? ''}
          onChange={(e) => onInput(sanitizeMoneyInput(e.target.value))}
          placeholder="0"
          className="border-secondary/50 font-display text-foreground placeholder:text-secondary/50 focus:border-secondary focus:ring-secondary w-full rounded-md border bg-transparent py-1.5 pr-2 pl-6 text-right text-base focus:ring-1 focus:outline-none"
        />
      </div>
    );
  }
  return (
    <span className="font-display text-foreground block text-right">
      {masked ? HIDDEN_AMOUNT : (display ?? formatMoney(value))}
    </span>
  );
}

function MoneyInput({
  label,
  sublabel,
  value,
  onChange,
}: {
  label: string;
  sublabel?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-card-foreground block text-sm font-semibold">{label}</span>
      {sublabel && <span className="text-muted-foreground mb-1.5 block text-xs">{sublabel}</span>}
      <div className="relative">
        <span
          className="text-secondary font-display absolute top-1/2 left-3 -translate-y-1/2 text-xl"
          aria-hidden
        >
          $
        </span>
        <input
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(sanitizeMoneyInput(e.target.value))}
          placeholder="0"
          className="border-secondary/30 bg-background/40 text-card-foreground placeholder:text-muted-foreground/60 focus:ring-secondary w-full rounded-md border py-2 pr-3 pl-9 text-lg font-semibold focus:ring-2 focus:outline-none"
        />
      </div>
    </label>
  );
}

function LiveTotal({ annual, twoYear }: { annual: number; twoYear: number }) {
  return (
    <div className="see-jesus-hero-card rounded-md px-5 py-3 text-center">
      <p className="text-secondary text-[11px] tracking-widest uppercase">
        Your Two-Year Commitment
      </p>
      <p className="font-display text-foreground mt-0.5 text-4xl">{formatMoney(twoYear)}</p>
      {annual > 0 && (
        <p className="text-secondary mt-0.5 text-[11px]">{formatMoney(annual)} annual × 2 years</p>
      )}
    </div>
  );
}

function Loading() {
  return (
    <div className="relative space-y-5">
      <LogoMark />
      <SectionTitle>See Jesus</SectionTitle>
      <div className="flex flex-wrap items-start justify-center gap-5">
        <SkeletonAvatar />
        <SkeletonAvatar />
        <SkeletonAvatar />
      </div>
      <div className="see-jesus-skeleton h-12 rounded-md" />
    </div>
  );
}

function SkeletonAvatar() {
  return (
    <div className="flex flex-col items-center">
      <div className="see-jesus-skeleton border-secondary/40 h-20 w-20 rounded-full border-2" />
      <div className="see-jesus-skeleton mt-2 h-3 w-16 rounded" />
    </div>
  );
}

function SignedOut() {
  // Clicking any of these sends them to sign in (MP's login element when the
  // page shows one, otherwise MP's hosted sign-in, which returns here). Once
  // `mpp-widgets_AuthToken` is in localStorage, subscribeToTokenChanges() or
  // the fresh page load re-runs loadProfile() to flip into the intro state.
  const handleClick = () => {
    triggerMPLogin();
  };
  return (
    <div className="relative space-y-5">
      <LogoMark />
      <SectionTitle>See Jesus</SectionTitle>
      <div className="flex flex-wrap items-start justify-center gap-5">
        <EmptyAddCircle onClick={handleClick} ariaLabel="Sign in to add yourself" />
        <EmptyAddCircle onClick={handleClick} ariaLabel="Sign in to add your spouse" />
      </div>
      <button onClick={handleClick} className="btn-double-outline">
        <span>Start Commitment</span>
        <ArrowRight className="arrow h-5 w-5" strokeWidth={1.5} />
      </button>
    </div>
  );
}

function EmptyAddCircle({ onClick, ariaLabel }: { onClick: () => void; ariaLabel: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col items-center focus:outline-none"
      aria-label={ariaLabel}
    >
      <div className="border-secondary/50 text-secondary/70 hover:border-secondary hover:text-secondary hover:bg-secondary/5 flex h-20 w-20 items-center justify-center rounded-full border-2 border-dashed transition-colors">
        <Plus className="h-7 w-7" strokeWidth={1.5} />
      </div>
      <span className="invisible mt-2 text-sm font-semibold select-none" aria-hidden>
        —
      </span>
    </button>
  );
}

function ErrorView({ message }: { message: string | null }) {
  return (
    <div className="border-destructive/40 rounded-md border p-6 text-center">
      <p className="text-destructive text-sm">{message ?? 'Something went wrong.'}</p>
    </div>
  );
}

// ───────────────────────── Small primitives ─────────────────────────

function GoldButton({
  children,
  onClick,
  disabled,
  compact,
}: {
  children: React.ReactNode;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  compact?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`bg-secondary text-secondary-foreground w-full rounded-md font-semibold ${
        compact ? 'py-2.5 text-sm' : 'py-3 text-base'
      } inline-flex items-center justify-center transition-all hover:opacity-95 active:scale-[0.98] disabled:opacity-50`}
    >
      {children}
    </button>
  );
}

function SmallIconButton({
  children,
  onClick,
  disabled,
  variant,
  label,
}: {
  children: React.ReactNode;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  variant?: 'primary' | 'danger';
  label: string;
}) {
  const base =
    'shrink-0 inline-flex items-center justify-center w-7 h-7 rounded transition-colors disabled:opacity-50';
  const styles =
    variant === 'primary'
      ? 'bg-secondary text-secondary-foreground hover:opacity-90'
      : variant === 'danger'
        ? 'text-destructive hover:bg-destructive/10'
        : 'text-secondary hover:bg-secondary/10';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`${base} ${styles}`}
    >
      {children}
    </button>
  );
}

// ───────────────────────── Helpers ─────────────────────────

/**
 * Decide the possessive + pluralization for the commitments list label:
 *   - "MY TWO-YEAR COMMITMENT"   one commitment, only one person involved
 *   - "MY TWO-YEAR COMMITMENTS"  multiple commitments, only one person involved
 *   - "OUR TWO-YEAR COMMITMENT(S)" any commitment involves a spouse OR the
 *     household has commitments under both contacts
 */
function commitmentsHeading(profile: ProfileResponse): string {
  const list = profile.existingCommitments;
  const people = new Set<number>();
  for (const c of list) {
    people.add(c.contactId);
    if (c.includeSpouseGiving && profile.spouse) {
      people.add(profile.spouse.contactId);
    }
  }
  const possessive = people.size > 1 ? 'Our' : 'My';
  const noun = list.length > 1 ? 'Commitments' : 'Commitment';
  return `${possessive} Two-Year ${noun}`;
}

function sanitizeMoneyInput(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
}

function parseMoney(raw: string): number {
  if (!raw) return 0;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The eye that hides and shows commitment amounts. It is the SAME switch as
 * the profile widget's "Hide amounts" (shared through the UI package), so
 * flipping it here flips it there, and the reverse.
 */
function HideAmountsToggle() {
  const hidden = useAmountsHidden();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setAmountsHidden(!hidden);
      }}
      aria-pressed={hidden}
      aria-label={hidden ? 'Show amounts' : 'Hide amounts'}
      title={hidden ? 'Show amounts' : 'Hide amounts'}
      className="border-secondary/40 text-secondary hover:border-secondary hover:text-foreground inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold tracking-widest uppercase transition-colors"
    >
      {hidden ? (
        <EyeOff className="h-3.5 w-3.5" strokeWidth={2} />
      ) : (
        <Eye className="h-3.5 w-3.5" strokeWidth={2} />
      )}
      {hidden ? 'Show amounts' : 'Hide amounts'}
    </button>
  );
}

function formatMoney(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Giving chart — opens as a responsive sheet (modal on desktop, bottom
// sheet on mobile) when the user clicks "See giving chart" from the
// form step. Used as a contextual hint while they're deciding the
// "See Jesus gift" amount.
// ─────────────────────────────────────────────────────────────────────

interface GivingChartRow {
  commitments: string;
  twoYear: string;
  annual: string;
  monthly: string;
  cumulative: string;
}

const GIVING_CHART_ROWS: GivingChartRow[] = [
  {
    commitments: '2',
    twoYear: '$5,000,000',
    annual: '$2,500,000',
    monthly: '$208,333',
    cumulative: '$10,000,000',
  },
  {
    commitments: '4',
    twoYear: '$2,000,000',
    annual: '$1,000,000',
    monthly: '$83,333',
    cumulative: '$18,000,000',
  },
  {
    commitments: '6',
    twoYear: '$1,000,000',
    annual: '$500,000',
    monthly: '$41,667',
    cumulative: '$24,000,000',
  },
  {
    commitments: '10',
    twoYear: '$500,000',
    annual: '$250,000',
    monthly: '$20,833',
    cumulative: '$29,000,000',
  },
  {
    commitments: '10',
    twoYear: '$250,000',
    annual: '$125,000',
    monthly: '$10,417',
    cumulative: '$31,500,000',
  },
  {
    commitments: '50',
    twoYear: '$100,000',
    annual: '$50,000',
    monthly: '$4,167',
    cumulative: '$36,500,000',
  },
  {
    commitments: '250',
    twoYear: '$50,000',
    annual: '$25,000',
    monthly: '$2,083',
    cumulative: '$49,000,000',
  },
  {
    commitments: '550',
    twoYear: '$25,000',
    annual: '$12,500',
    monthly: '$1,042',
    cumulative: '$62,750,000',
  },
  {
    commitments: '1,000',
    twoYear: '$10,000',
    annual: '$5,000',
    monthly: '$417',
    cumulative: '$72,750,000',
  },
  {
    commitments: '500',
    twoYear: '$5,000',
    annual: '$2,500',
    monthly: '$208',
    cumulative: '$76,500,000',
  },
  {
    commitments: '500',
    twoYear: '$2,500',
    annual: '$1,250',
    monthly: '$104',
    cumulative: '$79,000,000',
  },
  {
    commitments: '310',
    twoYear: '$1,250',
    annual: '$625',
    monthly: '$52',
    cumulative: '$79,775,000',
  },
  {
    commitments: 'All Others',
    twoYear: '$2,500 & Under',
    annual: '—',
    monthly: '—',
    cumulative: '$80,000,000',
  },
];

function GivingChartSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const foilUrl = `${ASSET_BASE_URL}/assets/GoldFoilBG.png`;
  const logoUrl = `${ASSET_BASE_URL}/assets/Logo.png`;

  // Custom header — foil-textured banner with the sunburst logo
  // absolute-positioned at the top-left, clipped by the header's
  // overflow:hidden so it peeks out of the corner without escaping the
  // sheet. Title + tagline stay centered.
  const header = (
    <div
      className="relative overflow-hidden px-4 pt-7 pb-5 text-center"
      style={{
        backgroundColor: '#000',
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 100%), url('${foilUrl}')`,
        backgroundSize: 'cover, 600px auto',
        backgroundPosition: 'center, center',
        backgroundRepeat: 'no-repeat, repeat',
      }}
    >
      <img
        src={logoUrl}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -top-5 -left-5 h-24 w-auto select-none"
        style={{ filter: 'drop-shadow(0 4px 14px rgba(199, 152, 84, 0.45))' }}
      />
      <h2 className="font-display text-secondary text-3xl leading-none tracking-wide">
        Giving Chart
      </h2>
      <p className="text-secondary/85 mx-auto mt-1.5 max-w-sm text-xs leading-snug italic">
        How different commitment levels combine to reach the $80M goal.
      </p>
    </div>
  );

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      maxWidth="max-w-5xl"
      panelClassName="bg-background text-foreground"
      sheetMaxHeight="85dvh"
      noPanelPadding
      header={header}
    >
      <SheetPage name="main">
        <div className="space-y-4 px-4 py-5">
          <div className="border-secondary/30 -mx-2 overflow-x-auto rounded border px-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-secondary/40 border-b-2">
                  <th className="text-secondary py-2 pr-2 text-left font-semibold tracking-wider uppercase">
                    Commit.
                  </th>
                  <th className="text-secondary py-2 pr-2 text-right font-semibold tracking-wider uppercase">
                    2-Year
                  </th>
                  <th className="text-secondary py-2 pr-2 text-right font-semibold tracking-wider uppercase">
                    Annual
                  </th>
                  <th className="text-secondary py-2 pr-2 text-right font-semibold tracking-wider uppercase">
                    Monthly
                  </th>
                  <th className="text-secondary py-2 text-right font-semibold tracking-wider uppercase">
                    Cumul.
                  </th>
                </tr>
              </thead>
              <tbody>
                {GIVING_CHART_ROWS.map((row, i) => (
                  <tr key={i} className="border-secondary/15 border-b last:border-b-0">
                    <td className="text-foreground py-2 pr-2 text-left">{row.commitments}</td>
                    <td className="text-foreground py-2 pr-2 text-right">{row.twoYear}</td>
                    <td className="text-foreground py-2 pr-2 text-right">{row.annual}</td>
                    <td className="text-foreground py-2 pr-2 text-right">{row.monthly}</td>
                    <td className="text-foreground py-2 text-right">{row.cumulative}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-secondary text-center text-xs leading-relaxed italic">
            "I will not offer burnt offerings to the Lord my God that cost me nothing." <br />
            <span className="text-secondary/70 text-[10px] tracking-widest not-italic">
              &mdash; 2 Samuel 24:24
            </span>
          </p>
        </div>
      </SheetPage>
    </ResponsiveSheet>
  );
}
